import { useCallback } from 'react';
import { ClientContainer, UserContainer } from '../../providers';

import { INDEXED_DB_CONFIGURATION, objectStores } from './constant';
import { createObjectStore, useIndexedDBInstance } from './indexed-db';
import { DB, Store } from './types';
import { validateBeforeTransaction } from './utils';

interface IIndexedDB {
  dbName?: string;
  version?: number;
  currentStore: Store;
}

interface IndexedDBProps {
  name: DB;
  dbVersion: number;
}

export const initDB = ({ name, dbVersion }: IndexedDBProps) => {
  Object.freeze(INDEXED_DB_CONFIGURATION);
  createObjectStore(name, dbVersion, objectStores);
};

export const useIndexedDB = ({
  currentStore,
  dbName = INDEXED_DB_CONFIGURATION.dbName,
  version = INDEXED_DB_CONFIGURATION.version,
}: IIndexedDB) => {
  const {
    openDatabase,
    createReadWriteDatabaseTransaction,
    createReadonlyDatabaseTransaction,
  } = useIndexedDBInstance(dbName, version);

  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const selectedClientId = clientState?.selectedClient?.id;
  const selectedBusinessId = userState?.business?.id;

  // CRUD operation for INDEXED DB
  const get = useCallback(
    async <T>(key: string, raw?: boolean) => {
      // get all
      return new Promise<T>((resolve, reject) => {
        openDatabase().then(db => {
          return new Promise((_, rej) => {
            validateBeforeTransaction(db, currentStore, rej);
            const { store } = createReadonlyDatabaseTransaction(
              db,
              currentStore,
              resolve,
              reject
            );

            const request = store.get(key);

            request.onerror = error => reject(error);

            request.onsuccess = (event: any) => {
              if (raw) {
                resolve(event.target.result);
                return;
              }
              const result =
                event.target.result?.[selectedBusinessId]?.[selectedClientId] ??
                event.target.result;
              resolve(result);
            };
          });
        });
      });
    },
    [
      createReadonlyDatabaseTransaction,
      currentStore,
      openDatabase,
      selectedBusinessId,
      selectedClientId,
    ]
  );

  const add = useCallback(
    async <T>(value: T, key?: string) => {
      return new Promise<T>((resolve, reject) => {
        openDatabase().then(db => {
          const { store } = createReadWriteDatabaseTransaction(
            db,
            currentStore,
            resolve as any,
            reject
          );

          const request = store.add(value, key);

          request.onerror = error => reject(error);

          request.onsuccess = (event: any) => {
            const resolveKey = event.target.result;
            resolve(resolveKey);
          };
        });
      });
    },
    [createReadWriteDatabaseTransaction, currentStore, openDatabase]
  );

  const update = useCallback(
    async <T>(value: T, key: string) => {
      return new Promise<T>((resolve, reject) => {
        openDatabase().then(db => {
          return new Promise((_, rej) => {
            validateBeforeTransaction(db, currentStore, rej);
            get<any>(key, true).then(res => {
              const { transaction, store } = createReadWriteDatabaseTransaction(
                db,
                currentStore,
                resolve as any,
                reject
              );

              const toUpdateValue = {
                ...(res ?? {}),
                [selectedBusinessId]: {
                  ...(res?.[selectedBusinessId] ?? {}),
                  [selectedClientId]: {
                    ...(res?.[selectedBusinessId]?.[selectedClientId] ?? {}),
                    ...value,
                  },
                },
              };

              store.put(toUpdateValue, key);

              transaction.oncomplete = (event: any) => resolve(event);
            });
          });
        });
      });
    },
    [
      createReadWriteDatabaseTransaction,
      currentStore,
      get,
      openDatabase,
      selectedBusinessId,
      selectedClientId,
    ]
  );

  const clear = useCallback(async () => {
    return new Promise<any>((resolve, reject) => {
      openDatabase().then(db => {
        return new Promise((_, rej) => {
          validateBeforeTransaction(db, currentStore, rej);
          objectStores.forEach(objectStore => {
            const { store, transaction } = createReadWriteDatabaseTransaction(
              db,
              objectStore.store,
              resolve as any,
              reject
            );

            transaction.oncomplete = () => resolve('');

            store.clear();
          });
        });
      });
    });
  }, [createReadWriteDatabaseTransaction, currentStore, openDatabase]);

  const deleteKey = useCallback(
    async (key: string) =>
      new Promise<any>((resolve, reject) => {
        openDatabase().then(db => {
          return new Promise((_, rej) => {
            validateBeforeTransaction(db, currentStore, reject);
            const { store } = createReadWriteDatabaseTransaction(
              db,
              currentStore,
              resolve as any,
              reject
            );
            const request = store.delete(key);

            request.onsuccess = event => resolve(event);
          });
        });
      }),
    [openDatabase, currentStore, createReadWriteDatabaseTransaction]
  );

  // TODO
  const getById = useCallback(() => {
    // get by id
  }, []);
  const openCursor = useCallback(() => {
    // open cursor
  }, []);
  const getByIndex = useCallback(() => {
    // get by index
  }, []);

  return {
    add,
    get,
    update,
    clear,
    deleteKey,
  };
};
